<template>
  <div class="auth">
    <div class="auth-wrapper">
      <VContainer>
        <VRow v-if="isLoading">
          <div class="d-flex flex-column align-center justify-center py-4 w-100">
            <VProgressCircular indeterminate color="primary" />
          </div>
        </VRow>
        <VRow v-else>
          <VCard v-if="isActiveInvite" flat class="w-100">
            <VCardTitle primary-title class="text-center">
              <h1>Request for Proposal</h1>
              <div class="mt-2">
                <VImg
                  v-if="inviteDetails.companyLogo"
                  cover
                  :src="inviteDetails.companyLogo"
                  max-height="200"
                  max-width="200"
                  class="mx-auto"
                />
              </div>
            </VCardTitle>
            <VCardItem>
              <VCardText>
                <VRow class="mb-4">
                  <VCol cols="6">
                    <VTextField
                      label="Industry"
                      prepend-inner-icon="mdi-account-outline"
                      variant="outlined"
                      color="primary"
                      name="industry"
                      v-model="inviteDetails.industry"
                      readonly
                    />
                  </VCol>
                  <VCol cols="6">
                    <VDateInput
                      label="Due Date"
                      v-model="inviteDetails.dueDate"
                      variant="outlined"
                      persistent-placeholder
                      prepend-inner-icon="mdi-calendar-clock"
                      prepend-icon=""
                      readonly
                    ></VDateInput>
                  </VCol>
                  <VCol cols="12">
                    <VTextarea
                      label="Description"
                      v-model="inviteDetails.description"
                      variant="outlined"
                      prepend-inner-icon="mdi-text"
                      readonly
                    ></VTextarea>
                  </VCol>
                </VRow>
                <VRow class="mb-4">
                  <VCol cols="12">
                    <div class="template__title py-2 px-2">
                      <h2>Details</h2>
                    </div>
                  </VCol>
                  <VCol cols="12">
                    <CategoriesList
                      :initialData="inviteDetails.mainQuestions"
                      type="Main Category"
                      :inviteId="inviteId"
                      is-preview
                    ></CategoriesList>
                  </VCol>
                </VRow>
              </VCardText>
            </VCardItem>
            <VCardItem>
              <VCardText>
                <VRow class="mb-4">
                  <VCol cols="12">
                    <div class="template__title py-2 px-2">
                      <h2>Questions</h2>
                    </div>
                  </VCol>
                  <VCol cols="12">
                    <CategoriesList
                      :initialData="inviteDetails.supplierQuestions"
                      type="Supplier Questions"
                      :inviteId="inviteId"
                    ></CategoriesList>
                  </VCol>
                  <VCol cols="12">
                    <div class="template__title py-2 px-2">
                      <h2>Additional Questions</h2>
                    </div>
                  </VCol>
                  <VCol cols="12">
                    <CategoriesList
                      :initialData="inviteDetails.supplierAdditionalQuestions"
                      type="Supplier Additional Questions"
                      :inviteId="inviteId"
                    ></CategoriesList>
                  </VCol>
                  <VCol cols="12">
                    <div class="template__title py-2 px-2">
                      <h2>Upload Files</h2>
                    </div>
                  </VCol>
                  <VCol cols="12">
                    <DocumentList
                      ref="uploadDocuments"
                      v-model="documents"
                      :recordId="inviteId"
                      :is-show-delete="!isPreview"
                    />
                    <UploadDocuments :recordId="inviteId" @success="handleDocumentsUploaded" />
                  </VCol>
                  <VCol cols="12">
                    <div class="mb-2 d-flex justify-end gap-2">
                      <VBtn :loading="isLoadingSubmit" type="submit" color="red" flat @click="handleSubmitNoBid()">
                        No-Bid
                      </VBtn>
                      <VBtn :loading="isLoadingSubmit" type="submit" color="primary" flat @click="submitInvite()">
                        Submit
                      </VBtn>
                    </div>
                  </VCol>
                </VRow>
              </VCardText>
            </VCardItem>
          </VCard>
          <VCard v-else rounded="md" elevation="10" max-width="500">
            <VCardItem class="pa-sm-8">
              <div class="d-flex flex-column align-center justify-center py-4">
                <Logo />
              </div>
              <div class="pb-5">
                <VCardTitle class="text-center">{{ inviteErrorTitle }}</VCardTitle>
                <VCardText>
                  <div class="d-flex flex-column align-center justify-center">
                    <v-icon color="error" size="48" class="mb-2">mdi-alert-circle</v-icon>
                    <p class="text-center">
                      {{ inviteErrorText }}
                    </p>
                  </div>
                </VCardText>
              </div>
              <div class="d-flex align-center justify-center">
                <router-link :to="{ name: 'Login' }" class="text-primary text-decoration-none">Go to Login</router-link>
              </div>
            </VCardItem>
          </VCard>
        </VRow>
      </VContainer>
    </div>
  </div>
  <SubmitNoBidInviteModal
    v-model="isShowNoBidModal"
    :invite-id="inviteId"
    @saved="getInviteDetails()"
  ></SubmitNoBidInviteModal>
</template>
<script>
import { mapActions } from 'vuex';
// Services
import invitesService from '@/services/invites-service/invites.service';
// Utils
import { formatNewRequest } from '@/utils/formatData';
// Components
import Logo from '@/components/common/Logo/Logo.vue';
import { VDateInput } from 'vuetify/labs/VDateInput';
import DocumentList from '@/components/common/DocumentList/DocumentList.vue';
import CategoriesList from '@/components/common/CategoriesList/CategoriesList.vue';
import UploadDocuments from '@/components/common/UploadDocuments/UploadDocuments.vue';
import SubmitNoBidInviteModal from '@/components/modals/SubmitNoBidInviteModal/SubmitNoBidInviteModal.vue';
// Text
import { successSubmitInviteText, errorSubmitInviteText } from '@/text/snackbarsText';
export default {
  name: 'InviteRequestPage',
  props: {
    hideText: Boolean
  },
  components: {
    VDateInput,
    CategoriesList,
    DocumentList,
    UploadDocuments,
    Logo,
    SubmitNoBidInviteModal
  },
  data() {
    return {
      inviteErrorTitle: 'Invite Not Found',
      inviteErrorText: 'Failed to load the RFP details. Please check your link or try again later.',

      isLoading: true,
      isActiveInvite: false,
      isLoadingSubmit: false,
      inviteId: this.$route.params.id,
      inviteDetails: {},
      documents: [],
      isShowNoBidModal: false
    };
  },
  mounted() {
    if (this.inviteId) {
      this.getInviteDetails();
    }
  },
  methods: {
    ...mapActions('main', ['showSnackbar', 'startLoading', 'stopLoading']),
    async getInviteDetails() {
      this.isActiveInvite = false;
      if (!this.inviteId) {
        return;
      }
      this.isLoading = true;
      try {
        const { inviteDetails } = await invitesService.getInviteDetails({ inviteId: this.inviteId });
        this.inviteDetails = formatNewRequest(inviteDetails);
        this.isActiveInvite = true;
      } catch (err) {
        console.error(err);
        const { message } = err;
        const errMessage = JSON.parse(message);
        if (errMessage?.message === 'Submitted') {
          this.inviteErrorTitle = 'Submission Already Received';
          this.inviteErrorText =
            'Thank you for your interest. We have already received your submission for this invitation';
        } else if (errMessage?.message === 'No-Bid') {
          this.inviteErrorTitle = 'No-Bid Decision Recorded';
          this.inviteErrorText = 'Thank you for informing us of your decision not to bid on this invite.';
        }
      } finally {
        this.isLoading = false;
      }
    },
    // Documents
    handleDocumentsUploaded(inviteId) {
      if (!inviteId) {
        return;
      }
      const documentComponent = this.$refs.uploadDocuments;
      console.log('Refresh Documents', documentComponent);
      if (documentComponent?.getDocuments) {
        console.log('Refresh Documents', documentComponent);
        documentComponent.getDocuments();
      }
    },
    // Validate Form
    validateForm() {
      const errors = [];

      this.inviteDetails.supplierQuestions.forEach((category, categoryIndex) => {
        const categoryName = category.value || `Category ${categoryIndex + 1}`;

        category.questions.forEach((question, questionIndex) => {
          const questionText = question.value || `Question ${questionIndex + 1}`;

          if (!question.answer.isNoBid) {
            if (!question.answer.supplierAnswer || question.answer.supplierAnswer.trim() === '') {
              errors.push(`Missing answer for "${questionText}" in "${categoryName}"`);
            }
          }
        });
      });

      this.inviteDetails.supplierAdditionalQuestions.forEach((category, categoryIndex) => {
        const categoryName = category.value || `Category ${categoryIndex + 1}`;

        category.questions.forEach((question, questionIndex) => {
          const questionText = question.value || `Question ${questionIndex + 1}`;

          if (!question.answer.isNoBid) {
            if (!question.answer.supplierAnswer || question.answer.supplierAnswer.trim() === '') {
              errors.push(`Missing answer for "${questionText}" in "${categoryName}"`);
            }
          }
        });
      });

      if (errors.length > 0) {
        this.showSnackbar({
          text: errors[0],
          color: 'error'
        });
        return false;
      }

      return true;
    },
    // No-Bid
    handleSubmitNoBid() {
      this.isShowNoBidModal = true;
    },
    // Submit
    async submitInvite() {
      if (!this.validateForm()) {
        return;
      }
      this.isLoadingSubmit = true;
      try {
        const payload = {
          inviteId: this.inviteId
        };
        await invitesService.submitInvite(payload);
        this.showSnackbar({ text: successSubmitInviteText, color: 'green' });
        this.getInviteDetails();
      } catch (err) {
        console.error(err);
        this.showSnackbar({ text: 'Error', color: 'red' });
        this.showSnackbar({ text: errorSubmitInviteText, color: 'red' });
      } finally {
        this.isLoadingSubmit = false;
      }
    }
  }
};
</script>
<style scoped>
.gap-1 {
  gap: 4px;
}

.gap-2 {
  gap: 8px;
}
</style>
